import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProject, useTransitionProject } from '../../hooks/useProjects';
import { useAuth } from '../../services/authContext';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import AssignmentManager from '../../components/domain/AssignmentManager';
import ProjectJoinRequestsPanel from '../../components/domain/ProjectJoinRequestsPanel';
import ProjectMonthLocksPanel from '../../components/domain/ProjectMonthLocksPanel';
import BackdatedHoursRequestsPanel from '../../components/domain/BackdatedHoursRequestsPanel';
import ProjectActions from '../../components/domain/ProjectActions';
import ProjectStatisticsSection from '../../components/domain/ProjectStatisticsSection';
import InvestmentSummarySection from '../../components/domain/InvestmentSummarySection';
import SubProjectsList from '../../components/domain/SubProjectsList';
import { getAncestorProjects } from '../../services/api/projects';
import ProjectCapacityGrid from '../../components/capacity/ProjectCapacityGrid';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import { useProjectStates } from '../../hooks/useProjectStates';
import { useProjectTemplate } from '../../hooks/useProjectTemplates';
import { StateHistory } from '../../components/workflow/StateHistory';
import { ProjectEngineeringStatsSection } from '../../components/engineering/ProjectEngineeringStatsSection';
import { BalicekHeroCard } from '../../components/projects/BalicekHeroCard';
import { DiscountConfigCard } from '../../components/projects/DiscountConfigCard';
import { OverageApprovalCard } from '../../components/projects/OverageApprovalCard';
import { UninvoicedHoursCard } from '../../components/projects/UninvoicedHoursCard';
import { ToBeInvoicedCard } from '../../components/projects/ToBeInvoicedCard';
import { ContractBillingSection } from '../../components/projects/ContractBillingSection';
import { useFlag } from '../../services/flagsContext';
import {
  SLA_BALICEK_PAYMENT_TYPE_ID,
  SLA_TASK_TYPE_ID,
  TIME_AND_MATERIAL_PAYMENT_TYPE_ID,
} from '../../constants/typeIds';
import { ProjectNotificationsTab } from './ProjectNotificationsTab';
// MKS-361 — "Out this month": who is away and who covers them on this project.
import ProjectCoveragePanel from '../../components/time-off/ProjectCoveragePanel';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';
import { labelChipStyle } from '../../utils/labelColor';

/**
 * Phase 72 (currency-aware budgets, Plan 04) — currency-aware Budget (Money)
 * header display, consistent with the ProjectStatisticsSection tracker bar.
 *
 * - CZK (or unknown) currency → unchanged CZK formatting (e.g. "50 000 Kč").
 * - Foreign currency → the amount in its own currency (e.g. "5 000 EUR"),
 *   plus the CZK equivalent from the 72-02 detail DTO
 *   (moneyTotalCzk / moneyTotalProvisional). Provisional (open period) reads
 *   "≈ X CZK provisional" (an estimate); locked reads "X CZK locked" (the
 *   materialized snapshot — no "≈" since it is exact). Conversion itself is
 *   NOT recomputed here — moneyTotalCzk is supplied by the backend.
 */
function formatMoneyBudget(project: {
  moneyTotal: number | null;
  moneyTotalCurrency?: 'CZK' | 'EUR' | 'USD';
  moneyTotalCzk?: number | null;
  moneyTotalProvisional?: boolean;
}): string {
  if (project.moneyTotal == null) return '-';
  const currency = project.moneyTotalCurrency;
  if (!currency || currency === 'CZK') {
    return formatCurrency(project.moneyTotal, false);
  }
  const amount = `${formatNumber(project.moneyTotal, { maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE)} ${currency}`;
  if (project.moneyTotalCzk == null) return amount;
  const czk = formatNumber(project.moneyTotalCzk, { maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE);
  return project.moneyTotalProvisional
    ? `${amount} (≈ ${czk} CZK provisional)`
    : `${amount} (${czk} CZK locked)`;
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isManager, isAdmin, isFinance, user } = useAuth();
  const canManage = isManager || isAdmin;
  // MKS-361 — the coverage panel below.
  const substitutesOn = useFlag('mks_361_time_off_substitutes');

  const projectId = id ? parseInt(id, 10) : undefined;
  const { data: project, isLoading, error } = useProject(projectId);
  const transitionMutation = useTransitionProject();
  const { data: auditTrail } = useAuditTrail('Project', projectId ?? 0);
  // User UAT 2026-06-10 — surface the project's current task-state list
  // on the read-only detail page so the user doesn't have to enter edit
  // mode (ProjectFormPage Task States section) to see which board columns
  // the project has. Mirrors the columns the user lands on at
  // /projects/:id/board.
  const { data: projectStates } = useProjectStates(projectId);
  const { data: appliedTemplate } = useProjectTemplate(
    project?.appliedTemplateId ?? undefined,
  );
  // F1-008: capture rejections from the transition mutation so the
  // inline-arrow `onClick={() => handleTransition('reopen')}` invocation
  // doesn't surface as an unhandled promise rejection (and the user gets
  // a visible error banner instead of silent failure).
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Fetch ancestor chain (parent hierarchy)
  const { data: ancestors } = useQuery({
    queryKey: ['projectAncestors', projectId],
    queryFn: () => getAncestorProjects(projectId!),
    enabled: !!projectId && !!project?.parent,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">
          {error ? `Failed to load project: ${(error as Error).message}` : 'Project not found.'}
        </p>
        <Link to="/projects" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to projects
        </Link>
      </div>
    );
  }

  const handleTransition = async (action: string, reason?: string) => {
    setTransitionError(null);
    try {
      await transitionMutation.mutateAsync({ id: project.id, action, reason });
    } catch (err) {
      setTransitionError((err as Error).message || `Failed to ${action} project`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">
              {project.name}
              {project.slug && (
                <span
                  className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-base font-mono font-semibold tracking-wide bg-gray-100 text-gray-700 uppercase align-middle"
                  title={`Project slug: ${project.slug}`}
                >
                  {project.slug}
                </span>
              )}
            </h1>
            <span className="text-sm text-gray-500">#{project.id}</span>
            <StatusBadge state={project.state} />
          </div>
          {project.fullName && project.fullName !== project.name && (
            <p className="text-gray-500 mt-1">{project.fullName}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Quick task 260512-p6d — primary access point to the Kanban board
              for this project. The route already exists (App.tsx → KanbanBoardPage)
              but had no link from anywhere; Petra reported 2026-05-12 that she
              "nevidí nikde u projektu ty projects [kanban]". Full ProjectDetailPage
              redesign per project-detail.html design handoff (Tasks tab as default
              board view) is deferred to a separate phase. */}
          <Button variant="primary" onClick={() => navigate(`/projects/${project.id}/board`)}>
            Tasks
          </Button>
          {canManage && project.state === 'archived' && (
            <Button
              variant="primary"
              onClick={() => handleTransition('reopen')}
              disabled={transitionMutation.isPending}
            >
              {transitionMutation.isPending ? 'Unarchiving...' : 'Unarchive'}
            </Button>
          )}
          <Link
            to={`/projects/${project.id}/edit?tab=engineering`}
            className="inline-flex items-center justify-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Engineering
          </Link>
          {canManage && (
            <>
              {(project._count?.children ?? 0) === 0 && (
                <Button variant="secondary" onClick={() => navigate(`/projects/new?parentId=${project.id}`)}>
                  New Subproject
                </Button>
              )}
              <Button variant="secondary" onClick={() => navigate(`/projects/${project.id}/edit`)}>
                Edit
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={() => navigate('/projects')}>
            Back
          </Button>
        </div>
      </div>

      <DismissibleErrorBanner message={transitionError} onDismiss={() => setTransitionError(null)} />

      {/* State transitions */}
      {canManage && (
        <ProjectActions
          state={project.state}
          onTransition={handleTransition}
          isLoading={transitionMutation.isPending}
        />
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Project Details</h2>
          {/*
            Plan 41-14 Task 0b — Restructured `<dl>` so `<dt>`/`<dd>` pairs are
            direct children (axe-core `dlitem` + `definition-list` rules). Layout
            now uses CSS Grid on the `<dl>` itself: full-row pairs span both
            columns; primary key/value pairs (Customer / PM / dates / rate /
            parent) take a single column. Visual layout preserved per the
            41-13 baseline (within maxDiffPixelRatio: 0.005 tolerance).
          */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {project.description && (
              <>
                <dt className="col-span-2 text-sm font-medium text-gray-500">Description</dt>
                <dd className="col-span-2 text-sm text-gray-900 -mt-2">{project.description}</dd>
              </>
            )}
            {project.labels && project.labels.length > 0 && (
              <>
                <dt className="col-span-2 text-sm font-medium text-gray-500">Labels</dt>
                <dd className="col-span-2 flex flex-wrap gap-2 -mt-2">
                  {project.labels.map((label) => (
                    <span
                      key={label.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        ...labelChipStyle(label.color),
                      }}
                    >
                      {label.name}
                    </span>
                  ))}
                </dd>
              </>
            )}
            <dt className="text-sm font-medium text-gray-500">Customer</dt>
            <dt className="text-sm font-medium text-gray-500">Project Manager</dt>
            <dd className="text-sm text-gray-900 -mt-2">{project.customer?.name ?? '-'}</dd>
            <dd className="text-sm text-gray-900 -mt-2">{project.projectManager?.name ?? '-'}</dd>
            <dt className="text-sm font-medium text-gray-500">Start Date</dt>
            <dt className="text-sm font-medium text-gray-500">End Date</dt>
            <dd className="text-sm text-gray-900 -mt-2">{project.startDate ? formatDisplayDate(project.startDate) : '-'}</dd>
            <dd className="text-sm text-gray-900 -mt-2">{project.endDate ? formatDisplayDate(project.endDate) : '-'}</dd>
            {/* Phase 72 (currency-aware budgets, Plan 04) — the "Default Rate"
                row was removed from the UI (user decision; the backend column
                + its retirement are a separate follow-up). "Parent Project"
                now spans the full width so the 2-col dl stays balanced. */}
            <dt className="text-sm font-medium text-gray-500 col-span-2">Parent Project</dt>
            <dd className="text-sm text-gray-900 -mt-2 col-span-2">
              {project.parent ? (
                <div className="space-y-0.5">
                  {/* Show ancestors from root to immediate parent with indentation */}
                  {ancestors && [...ancestors].reverse().map((ancestor, index) => (
                    <div key={ancestor.id} style={{ paddingLeft: `${index * 12}px` }}>
                      {index > 0 && <span className="text-gray-300 mr-1">└</span>}
                      <Link to={`/projects/${ancestor.id}`} className="text-primary-600 hover:underline">
                        {ancestor.name}
                      </Link>
                    </div>
                  ))}
                </div>
              ) : '-'}
            </dd>
            {project.timeReportingDisabled && (
              <>
                <dt className="col-span-2 text-sm font-medium text-gray-500">Time Reporting</dt>
                <dd className="col-span-2 -mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-300">
                    Disabled — hours cannot be logged on this project
                  </span>
                </dd>
              </>
            )}
            {/* Phase 72 Plan 04 (CUR-02/CUR-06) — hide "Budget (Money)"
                (moneyTotal) for balíček projects: the balíček paušál shown in
                the Hour Package card is the relevant figure, and moneyTotal is
                an unrelated leftover. Kept for Default/Fixed Price/T&M. */}
            {(() => {
              const isBalicekProject = project.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID;
              const showHours = project.hoursTotal != null;
              const showMoney = project.moneyTotal != null && !isBalicekProject;
              if (!showHours && !showMoney) return null;
              const dtClass = 'text-sm font-medium text-gray-500 pt-2 border-t border-gray-100';
              // MKS-263 — on T&M the same moneyTotal column is the estimate /
              // maximum promised to the client, which is what the project form
              // calls it and what the consumption alerts measure against.
              // "Budget (Money)" reads as a cap there, and it is not one.
              const moneyLabel =
                project.paymentTypeId === TIME_AND_MATERIAL_PAYMENT_TYPE_ID
                  ? 'Estimated maximum'
                  : 'Budget (Money)';
              if (showHours && showMoney) {
                return (
                  <>
                    <dt className={dtClass}>Budget (Hours)</dt>
                    <dt className={dtClass}>{moneyLabel}</dt>
                    <dd className="text-sm text-gray-900 -mt-2">{`${project.hoursTotal} hrs`}</dd>
                    <dd className="text-sm text-gray-900 -mt-2">{formatMoneyBudget(project)}</dd>
                  </>
                );
              }
              // Only one budget to show → render it full-width so the 2-col
              // dl stays balanced (no orphaned empty cell).
              return showHours ? (
                <>
                  <dt className={`${dtClass} col-span-2`}>Budget (Hours)</dt>
                  <dd className="text-sm text-gray-900 -mt-2 col-span-2">{`${project.hoursTotal} hrs`}</dd>
                </>
              ) : (
                <>
                  <dt className={`${dtClass} col-span-2`}>{moneyLabel}</dt>
                  <dd className="text-sm text-gray-900 -mt-2 col-span-2">{formatMoneyBudget(project)}</dd>
                </>
              );
            })()}
          </dl>
        </div>

        {/* UAT 2026-05-13 F-D1 — BalicekHeroCard moved into the 2-column grid
            so the SLA Hour Package sits next to Project Details (was below).
            F-D4 — OverageApprovalCard stacked below the hero card (also in
            the 2nd column) since it's tightly coupled to the package state.
            Both hide themselves when not on the SLA Balíček payment template
            so non-SLA projects leave the second column empty. */}
        {projectId && (
          <div className="space-y-6">
            <BalicekHeroCard
              projectId={projectId}
              paymentTypeId={project.paymentTypeId ?? null}
              slaBalicekPaymentTypeId={SLA_BALICEK_PAYMENT_TYPE_ID}
              initialBalicekAmount={project.balicekAmount ?? null}
              // Phase 72 Plan 04 (CUR-02/CUR-06) — provisional CZK preview for
              // the Monthly fee row.
              balicekAmountCzk={project.balicekAmountCzk}
              balicekAmountProvisional={project.balicekAmountProvisional}
              canManage={canManage}
            />
            {/* Phase 110 Plan 110-08 (D-08) — write controls gated on isAdmin
                ALONE, never `canManage` (isManager || isAdmin) as used by the
                sibling cards above/below. A discount is a commercial
                agreement the server reserves to admins to write; a manager
                would see controls the server rejects with 403. Rendered
                unconditionally (no admin-only wrapper) because a project PM
                must be able to read the discount to explain the invoice,
                matching the server's requireAdminOrProjectMemberByProject
                read gate. */}
            <DiscountConfigCard
              projectId={projectId}
              paymentTypeId={project.paymentTypeId ?? null}
              slaBalicekPaymentTypeId={SLA_BALICEK_PAYMENT_TYPE_ID}
              canManage={isAdmin}
            />
            <OverageApprovalCard
              projectId={projectId}
              paymentTypeId={project.paymentTypeId ?? null}
              overLimitAllowed={project.overLimitAllowed ?? false}
              slaBalicekPaymentTypeId={SLA_BALICEK_PAYMENT_TYPE_ID}
              canManage={canManage}
            />
            {/* Hide the uninvoiced-hours card on non-billable projects (mirrors the
                Phase 62 billable gate applied to ToBeInvoicedCard immediately below). */}
            {project.billable === true && (
              <UninvoicedHoursCard
                projectId={projectId}
                paymentTypeId={project.paymentTypeId ?? null}
                slaBalicekPaymentTypeId={SLA_BALICEK_PAYMENT_TYPE_ID}
              />
            )}
            {/* Phase 62 (Plan 62-06, TBI-01, D-07) — To Be Invoiced builder.
                Rendered only when the project has the billable flag set; the
                existing balíček/uninvoiced cards stay untouched regardless.
                Review fix WR-06: billing data (rates, paushal, invoice ledger)
                is restricted to admin/finance/PM, mirroring the backend gate.
                billedAmount edits + invoice generation stay admin/finance
                only (canEdit). */}
            {project.billable === true &&
              (isAdmin ||
                isFinance ||
                (user != null && project.projectManager?.id === user.id)) && (
                <ToBeInvoicedCard projectId={projectId} canEdit={isAdmin || isFinance} />
              )}
          </div>
        )}
      </div>

      {/* MKS-137 — Contract & billing: how this project is billed, in one
          read-only place. Everything in it had an editor already (the project
          form's Billing terms and Billing schedule sections) and nothing had a
          reader, so a PM had to enter edit mode to see their own commercial
          terms. The Edit link points back at that form.

          `canEdit` mirrors the backend's write gate on these columns (admin,
          finance, or this project's PM — PATCH /api/projects/:id/billing-terms);
          `canOpenInvoice` is narrower because /invoices/:id is <FinanceRoute>. */}
      <ContractBillingSection
        project={project}
        canEdit={isAdmin || isFinance || (user != null && project.projectManager?.id === user.id)}
        canOpenInvoice={isAdmin || isFinance}
      />


      {/* Sub-projects list */}
      {projectId && (
        <SubProjectsList projectId={projectId} childCount={project._count?.children ?? 0} showAddButton={canManage} />
      )}

      {/* Project Statistics & Investment Summary */}
      {canManage && projectId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProjectStatisticsSection
            projectId={projectId}
            hasSubprojects={(project._count?.children ?? 0) > 0}
            hoursBudget={project.hoursTotal}
            moneyBudget={project.moneyTotal}
            // Phase 72 (currency-aware budgets, Plan 02/04) — drives the
            // Money Budget tracker's provisional/locked marker + the
            // CZK-equivalent denominator for a foreign-currency budget.
            moneyBudgetCzk={project.moneyTotalCzk}
            moneyBudgetProvisional={project.moneyTotalProvisional}
            moneyBudgetCurrency={project.moneyTotalCurrency}
          />
          <InvestmentSummarySection projectId={projectId} />
        </div>
      )}

      {/* Phase 46 Plan 46-08 — Notifications card (per-project routing).
          Component decides whether to render based on (isAdmin || isProjectPM);
          non-PM team members receive null. Moved below the Project Statistics &
          Investment Summary grid per user request 2026-07-07. */}
      {projectId && (
        <ProjectNotificationsTab
          projectId={projectId}
          project={{ projectManagerId: project.projectManager?.id ?? null }}
        />
      )}

      {/* Petra UAT 2026-05-20 — SLA priority tiers (P1/P2/...) are not editable
          from the project form. The admin page at /admin/sla-tiers owns the per-tier CRUD and
          deep-links via ?projectId so this jump pre-selects the project.
          Review WR-01: gated on `isAdmin` (not canManage) because the
          destination route is <AdminRoute> — a pure manager clicking this
          would land on /unauthorized. */}
      {isAdmin && projectId && project.taskTypeId === SLA_TASK_TYPE_ID && (
        <div className="rounded-lg border border-ms-border-light bg-white p-4">
          <Link
            to={`/admin/sla-tiers?projectId=${projectId}`}
            className="text-[13px] font-medium text-ms-accent hover:underline"
          >
            Manage SLA priority tiers →
          </Link>
        </div>
      )}

      {/* Task States — read-only summary of the project's board columns.
          Edit affordances live on /projects/:id/edit (StateListEditor) and
          /projects/:id/board (Quick task 260610-kv6 — column headers +
          AddStateColumn). */}
      {projectId && projectStates && projectStates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Task States</h2>
              {appliedTemplate?.name && (
                <p className="text-sm text-gray-500 mt-1">
                  Most recently applied: <span className="font-medium text-gray-700">{appliedTemplate.name}</span>
                </p>
              )}
            </div>
            {canManage && (
              <Link
                to={`/projects/${project.id}/board`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Manage on board →
              </Link>
            )}
          </div>
          <ul className="flex flex-wrap gap-2">
            {projectStates.map((s) => (
              <li
                key={s.id}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50"
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: s.color }}
                  aria-hidden
                />
                <span className="text-sm text-gray-800">{s.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Capacity Planning */}
      {canManage && projectId && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Capacity Planning</h2>
          <ProjectCapacityGrid projectId={projectId} />
        </div>
      )}

      {/* Assignments */}
      {canManage && projectId && (
        <AssignmentManager projectId={projectId} />
      )}

      {/* MKS-361 — who is off this month and who covers them here. Visible to
          anyone who can see the project, not only the PM: the point of a
          coverage view is that the team knows who to ask. */}
      {substitutesOn && projectId && <ProjectCoveragePanel projectId={projectId} />}

      {/* Join Requests — contextual panel for managers/admins; hides itself when no pending requests exist */}
      {canManage && projectId && (
        <ProjectJoinRequestsPanel projectId={projectId} isAdmin={isAdmin} />
      )}

      {/* Phase 113 (MKS-208, D-13) — the padlock, per project-month, where the
          PM already works. Visible to anyone who can see the project so the
          state is never a surprise; only an admin or this project's PM can
          actually flip it. */}
      {projectId && (
        <ProjectMonthLocksPanel
          projectId={projectId}
          canManage={isAdmin || project.projectManager?.id === user?.id}
        />
      )}

      {/* Phase 113 (MKS-208, D-10) — the project-scoped half of the approval
          surface; hides itself when nothing is pending, like join requests. */}
      {canManage && projectId && (
        <BackdatedHoursRequestsPanel projectId={projectId} isAdmin={isAdmin} />
      )}

      {/* Phase 129 (D-14) — repositories are linked on the Engineering tab. */}
      {canManage && projectId && (
        <div
          data-testid="repositories-moved-card"
          className="bg-white rounded-lg shadow p-4 text-sm text-gray-700"
        >
          Linked GitHub repositories are managed on the{' '}
          <Link
            to={`/projects/${projectId}/edit?tab=engineering`}
            className="text-primary-600 hover:underline"
          >
            Engineering tab
          </Link>
          .
        </div>
      )}

      {/* quick-260513-bx4 — Project-detail Engineering Activity panel. */}
      {canManage && projectId && (
        <ProjectEngineeringStatsSection projectId={projectId} />
      )}

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>

    </div>
  );
}
